import { useState } from "react";
import { Box, Drawer, IconButton, Typography, Button, Chip, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AlertConfirmationDialog from "components/ui/AlertConfirmationDialog";
import { Icon } from "@fluentui/react/lib/Icon";
import { IAlertFormData } from "system/types/interfaces/IAlertFormData ";
import "../../styles/manage-alert.css";
import SuccessMessage from "components/ui/SuccessMessage";
import { sanitizeHtml } from "system/utils/sanitizeHtml";

interface AlertDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  alert: IAlertFormData | null;
  onDisable: (alertId: number) => void;
  onSaveAlert: (data: IAlertFormData) => void;
  setRefreshAlerts: any;
  onEdit: (alert: IAlertFormData) => void;
}

export const AlertDetailDrawer: React.FC<AlertDetailDrawerProps> = ({
  open,
  onClose,
  alert,
  onDisable,
  onSaveAlert,
  setRefreshAlerts,
  onEdit,
}) => {
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [successMessageTitle, setSuccessMessageTitle] = useState("");

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "Urgent":
        return "#f44336";
      case "Warning":
        return "#ff9800";
      case "Info":
        return "#2196f3";
      default:
        return "#757575";
    }
  };

  const handleDisable = () => {
    setConfirmationDialogVisible(true);
  };

  const handleConfirmDisable = () => {
    setConfirmationDialogVisible(false);
    onDisable(alert!.id!);
    setSuccessMessageTitle("Alert disabled successfully");
    setSuccessMessageVisible(true);
    onClose();
  };

  if (!alert) return null;

  return (
    <>
      <Drawer anchor="right" open={open} onClose={onClose}>
        <div className="notification-drawer">
          <div className="drawer-header">
            <Typography variant="h6" fontWeight="bold">
              {alert.alertTitle}
            </Typography>
            <IconButton onClick={onClose} sx={{ color: "#ffffff" }}>
              <CloseIcon />
            </IconButton>
          </div>

          <div className="drawer-body" style={{ padding: "24px", color: "#ffffff", height: "100%" }}>
            <Box mb={2} display="flex" alignItems="center" gap={1}>
              <Chip
                label={alert.severity}
                sx={{
                  bgcolor: getSeverityColor(alert.severity),
                  color: "#fff",
                  padding: "6px",
                  borderRadius: "4px",
                  fontSize: "14px",
                }}
              />
              <Typography variant="body2" color="#aaa">
                {alert.startDate} → {alert.endDate}
              </Typography>
            </Box>

            <Typography variant="subtitle1" mb={1}>
              Description
            </Typography>
            <div
              style={{ marginBottom: "1.5rem", color: "#ccc", lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(alert.description) }}
            />
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button
                variant="contained"
                onClick={() => onEdit(alert!)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontSize: "14px",
                  padding: "6px 12px",
                }}
              >
                Edit
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Icon iconName="edit" />
                </Box>
              </Button>

              <Button
                variant="outlined"
                color="secondary"
                onClick={handleDisable}
                disabled={alert?.status === "Disabled"}
                sx={{
                  backgroundColor: alert?.status === "Disabled" ? "#3B3A39" : "none",
                  color: alert?.status === "Disabled" ? "#8A8886" : "none",
                  fontSize: "14px",
                  padding: "6px 12px",
                  cursor: alert?.status === "Disabled" ? "not-allowed" : "pointer",
                }}
              >
                Disable
              </Button>
            </Stack>
          </div>
        </div>
      </Drawer>

      <AlertConfirmationDialog
        title="Disable Alert"
        subText="Are you sure you want to disable this alert?"
        isVisible={confirmationDialogVisible}
        onConfirm={handleConfirmDisable}
        onCancel={() => setConfirmationDialogVisible(false)}
      />
      <SuccessMessage
        isVisible={successMessageVisible}
        onConfirm={() => {
          setSuccessMessageVisible(false);
        }}
        title={successMessageTitle}
        message="Your changes have been saved."
      />
    </>
  );
};
